import moment from 'moment';

const TIMESTAMP_FORMATS = [
	'DD/MM/YYYY HH:mm:ss',
	'DD/MM/YYYY HH:mm',
	'DD/MM/YYYY',
	'D/M/YYYY HH:mm:ss',
	'D/M/YYYY HH:mm',
	'D/M/YYYY',
	'DD/MM/YY HH:mm:ss',
	'DD/MM/YY HH:mm',
	'DD/MM/YY',
	'D/M/YY HH:mm:ss',
	'D/M/YY HH:mm',
	'D/M/YY',
	'YYYY-MM-DDTHH:mm:ss',
	'YYYY-MM-DD HH:mm:ss',
	'YYYY-MM-DD HH:mm',
	'YYYY-MM-DD',
];
const AUGUST = 7;

export const parseAdditionalTimestamp = (timestamp: string): moment.Moment | undefined => {
	const parsed = moment(timestamp, [...TIMESTAMP_FORMATS, moment.ISO_8601], true);
	return parsed.isValid() ? parsed : undefined;
};

/**
 * Academic years run from 1 August to 31 July.
 * Additional download timestamps are typically DD/MM/YY, but other date shapes are accepted
 * so a row is not silently dropped when the API includes a time or ISO date.
 */
export const getAcademicYearFromAdditionalTimestamp = (timestamp: string): number | undefined => {
	const parsed = parseAdditionalTimestamp(timestamp);

	if (!parsed) {
		return undefined;
	}

	const year = parsed.year();
	return parsed.month() >= AUGUST ? year : year - 1;
};

export const getAdditionalDownloadAcademicYear = (
	download: Report.Additional
): number | undefined =>
	typeof download.AcademicYear === 'number'
		? download.AcademicYear
		: getAcademicYearFromAdditionalTimestamp(download.Timestamp);

export const withAdditionalAcademicYear = (
	download: Report.Additional,
	fallbackYear?: number
): Report.Additional => ({
	...download,
	AcademicYear: getAdditionalDownloadAcademicYear(download) ?? fallbackYear,
});

export const resolveAdditionalAvailableYears = (
	yearsFromApi: number[],
	downloads: Report.Additional[]
): number[] => {
	const yearsFromJobs = downloads
		.map((download) => getAdditionalDownloadAcademicYear(download))
		.filter((year): year is number => typeof year === 'number');

	return [...new Set([...yearsFromApi, ...yearsFromJobs])].sort((a, b) => b - a);
};

export const compareAdditionalDownloadsByDate = (
	left: Report.Additional,
	right: Report.Additional
): number => {
	const leftTime = parseAdditionalTimestamp(left.Timestamp)?.valueOf() ?? 0;
	const rightTime = parseAdditionalTimestamp(right.Timestamp)?.valueOf() ?? 0;
	return rightTime - leftTime;
};

export const getAdditionalDownloadLabel = ({ Subject, Uploader }: Report.Additional): string =>
	Uploader ? `${Subject} - ${Uploader}` : Subject;
